/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { ApolloError } from "@apollo/client";
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from "next";
import { getNavigationItems } from "src/services/graphql/get-navigation-items";
import { getProductBySlug } from "src/services/graphql/get-product-by-slug";
import { getSeoContent } from "src/services/graphql/get-seo-content";
import { PromotionContextProvider } from "src/contexts/promotion.context";
import { ProductView } from "src/organisms/ProductView";
import {
  NavigationItems,
  SeoQueryResponse,
  SingleProduct,
} from "src/types/graphql.d";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { useProductSchema } from "src/hooks/useProductSchema";
import { DEFAULT_PAGE_NOT_FOUND } from "src/utils/constants";
import { buildStaticProps } from "src/utils/static-props/app-page-props";

type ProductPageProps = {
  product?: SingleProduct;
  error: ApolloError | string | null;
  loading: boolean;
  slug: string;
  gender: string | null;
  navigationItems?: NavigationItems | null;
  clientNavigation: boolean;
  seoContent?: SeoQueryResponse | null;
};

const ProductPage = ({
  pageProps: { product, error, loading = true, slug, seoContent },
}: {
  pageProps: ProductPageProps;
}): JSX.Element => {
  // Generate schema for the product
  useProductSchema(product, slug);

  return (
    <PromotionContextProvider>
      <ProductView
        product={product}
        productError={error}
        productLoading={loading}
        slug={slug}
        seoContent={seoContent}
      />
    </PromotionContextProvider>
  );
};

export function getStaticPaths(): GetStaticPathsResult {
  return {
    paths: [],
    fallback: "blocking",
  };
}

export async function getStaticProps({
  params,
}: GetStaticPropsContext<{
  productSlug: string;
}>): Promise<GetStaticPropsResult<ProductPageProps>> {
  if (!params || !params.productSlug) {
    return DEFAULT_PAGE_NOT_FOUND;
  }

  const slug = params.productSlug;
  const product = await getProductBySlug(slug);

  if (!product) {
    return DEFAULT_PAGE_NOT_FOUND;
  }

  const isMultipleShopType = (product.product_shop_type?.length || 0) > 1;
  const gender = isMultipleShopType
    ? null
    : product.gender?.toLowerCase() || null;

  const [{ navigationItems }, seoContent] = await Promise.all([
    getNavigationItems(
      localeFix({
        gender,
      })
    ),
    getSeoContent(`/product/${slug}`),
  ]);

  return buildStaticProps(
    {
      product,
      loading: false,
      error: null,
      slug,
      clientNavigation: isMultipleShopType, // Temporary fix for cart because of genderless pages
      navigationItems: isMultipleShopType ? null : navigationItems, // If it's a unisex load navigation from client side
      gender,
      seoContent,
    },
    60 * 15
  );
}

export default ProductPage;
